import { CORES_ABNT_FIBRA, CORES_EIA598_FIBRA } from './fiberColors';
import type { CaboTipo, PadraoTuboLoose } from '../services/caboService';
import { CABO_QTDE_FO_OPCOES } from '../services/caboService';

/** Cores de fibra EIA/TIA-598-A */
export { CORES_EIA598_FIBRA };

export const CORES_ABNT_TUBO = CORES_ABNT_FIBRA;
export const CORES_EIA598_TUBO = CORES_EIA598_FIBRA;

export const MAX_TUBOS_LOOSE = 12;

/** Divisores válidos de qtde_fo para quantidade de tubos (1 a 12) */
export function getOpcoesQtdeTuboLoose(qtdeFo: number): number[] {
  const opcoes: number[] = [];
  for (let i = 1; i <= Math.min(MAX_TUBOS_LOOSE, qtdeFo); i++) {
    if (qtdeFo % i === 0) opcoes.push(i);
  }
  return opcoes;
}

export function getFibrasPorTubo(qtdeFo: number, qtdeTuboLoose: number, tipo?: CaboTipo | null): number {
  const tubos = tipo === 'asu' ? 1 : Math.max(1, qtdeTuboLoose);
  return Math.floor(qtdeFo / tubos);
}

export function getCorTuboLoose(numeroTubo: number, padrao: PadraoTuboLoose) {
  const cores = padrao === 'eia598a' ? CORES_EIA598_TUBO : CORES_ABNT_TUBO;
  return cores[(numeroTubo - 1) % 12];
}

/** Cor da fibra pela posição dentro do tubo loose */
export function getCorFibraNoCabo(
  numeroFibra: number,
  padrao: PadraoTuboLoose,
  fibrasPorTubo: number,
) {
  const posicaoNoTubo = ((numeroFibra - 1) % fibrasPorTubo) + 1;
  const ciclo = padrao === 'eia598a' ? 6 : 12;
  const indice = (posicaoNoTubo - 1) % ciclo;
  const cores = padrao === 'eia598a' ? CORES_EIA598_FIBRA : CORES_ABNT_FIBRA;
  return cores[indice];
}

export interface TuboLooseGrupo {
  tuboNum: number;
  corTubo: { cor: string; nome: string };
  fibras: number[];
  inicio: number;
  fim: number;
  fibrasPorTubo: number;
}

export function getTubosLoose(
  qtdeFo: number,
  qtdeTuboLoose: number,
  padrao: PadraoTuboLoose = 'abnt',
  tipo?: CaboTipo | null,
): TuboLooseGrupo[] {
  const totalTubos = tipo === 'asu' ? 1 : Math.max(1, qtdeTuboLoose);
  const fibrasPorTubo = getFibrasPorTubo(qtdeFo, totalTubos, tipo);

  return Array.from({ length: totalTubos }, (_, g) => {
    const tuboNum = g + 1;
    const inicio = g * fibrasPorTubo + 1;
    const fim = Math.min(inicio + fibrasPorTubo - 1, qtdeFo);
    const fibras = Array.from({ length: Math.max(0, fim - inicio + 1) }, (_, i) => inicio + i);
    return {
      tuboNum,
      corTubo: getCorTuboLoose(tuboNum, padrao),
      fibras,
      inicio,
      fim,
      fibrasPorTubo,
    };
  }).filter((t) => t.fibras.length > 0);
}

/** Sugestão padrão de tubos ao mudar qtde_fo (ex.: 72 FO → 6 tubos de 12 FO) */
export function sugerirQtdeTuboLoose(qtdeFo: number, tipo?: CaboTipo | null): number {
  if (tipo === 'asu') return 1;
  if (qtdeFo % 12 === 0) {
    const por12 = qtdeFo / 12;
    if (por12 >= 1 && por12 <= MAX_TUBOS_LOOSE) return por12;
  }
  const opcoes = getOpcoesQtdeTuboLoose(qtdeFo);
  return opcoes[0] ?? 1;
}

export function validarCaboTuboLoose(
  qtdeFo: number,
  qtdeTuboLoose: number,
  tipo?: CaboTipo | null,
  padrao?: PadraoTuboLoose | null,
): string | null {
  if (!(CABO_QTDE_FO_OPCOES as readonly number[]).includes(qtdeFo)) {
    return 'Selecione uma quantidade de fibras válida';
  }

  if (tipo === 'asu') {
    if (qtdeFo > 12) return 'Cabo ASU possui apenas 1 tubo loose com no máximo 12 fibras';
    if (qtdeTuboLoose !== 1) return 'Cabo ASU possui somente 1 tubo loose';
    if (padrao && padrao !== 'abnt') return 'Cabo ASU utiliza somente o padrão de cores ABNT';
    return null;
  }

  if (qtdeTuboLoose < 1 || qtdeTuboLoose > MAX_TUBOS_LOOSE) {
    return `Quantidade de tubos loose deve ser entre 1 e ${MAX_TUBOS_LOOSE}`;
  }

  if (qtdeFo % qtdeTuboLoose !== 0) {
    return 'As fibras devem dividir igualmente entre os tubos loose';
  }

  const fibrasPorTubo = qtdeFo / qtdeTuboLoose;
  if (fibrasPorTubo < 1) return 'Cada tubo loose deve ter ao menos 1 fibra';

  return null;
}
